import type { CSSProperties } from 'react';
import { createContext, useCallback, useContext, useRef } from 'react';

type LiveRegionAnnouncerContextType = {
  announce: (msg: string) => void;
};

const LiveRegionAnnouncerContext = createContext<LiveRegionAnnouncerContextType | undefined>(undefined);

const liveAnnouncerRoot: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  clip: 'rect(1px, 1px, 1px, 1px)',
  padding: 0,
  border: 0,
  height: 1,
  width: 1,
  overflow: 'hidden',
  zIndex: -9999,
};

export const LiveRegionAnnouncerProvider = ({ children }) => {
  const liveAnnouncerRef = useRef<HTMLDivElement>(null);

  const timeoutRef = useRef<number | ReturnType<typeof setTimeout> | null>(null);
  const announce = useCallback((msg) => {
    // 💡 Programmatically focus the live announcer after a small delay.
    if (liveAnnouncerRef.current) {
      liveAnnouncerRef.current.textContent = msg;

      // 💡 Wait a fraction of a second, then focus the element.
      setTimeout(() => {
        if (liveAnnouncerRef.current) {
          liveAnnouncerRef.current.focus();
        }
      }, 10);
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      if (liveAnnouncerRef.current) {
        liveAnnouncerRef.current.textContent = '';
      }
    }, 5000);
  }, []);

  const value = { announce };

  return (
    <LiveRegionAnnouncerContext.Provider value={value}>
      {children}
      <div
        id="live-announcer-root"
        ref={liveAnnouncerRef}
        role="status"
        aria-live="polite"
        tabIndex={-1} // 👈 ensure the element is focusable
        style={liveAnnouncerRoot}
      ></div>
    </LiveRegionAnnouncerContext.Provider>
  );
};

export const useAnnouncer = () => {
  const context = useContext(LiveRegionAnnouncerContext);

  if (!context) {
    throw new Error('useAnnouncer must be used within an AnnouncerProvider');
  }

  return context;
};
